import MODULE_CATALOG_JSON from '../shared/tenantModules.json';

export const MODULE_AVAILABILITY = Object.freeze({
  ENABLED: 'enabled',
  DISABLED: 'disabled',
  COMING_SOON: 'coming_soon',
  DEPRECATED: 'deprecated'
});

function normalizeCatalog(catalog = MODULE_CATALOG_JSON) {
  return Object.freeze(
    (Array.isArray(catalog) ? catalog : []).map((module) => Object.freeze({
      ...module,
      dependencies: Object.freeze(Array.isArray(module.dependencies) ? module.dependencies : []),
      featureFlags: Object.freeze(Array.isArray(module.featureFlags) ? module.featureFlags : []),
      locked: module.locked === true,
      defaultEnabled: module.defaultEnabled !== false
    }))
  );
}

function buildModulesById(catalog) {
  return Object.freeze(
    catalog.reduce((accumulator, module) => {
      accumulator[module.id] = module;
      return accumulator;
    }, {})
  );
}

export function validateModuleCatalog(catalogInput = MODULE_CATALOG_JSON) {
  const catalog = normalizeCatalog(catalogInput);
  const modulesById = buildModulesById(catalog);
  const seenIds = new Set();
  const stack = new Set();
  const visited = new Set();

  catalog.forEach((module) => {
    const moduleId = String(module?.id || '').trim();
    if (!moduleId) {
      throw new Error('Tenant module catalog contains a module without an id.');
    }
    if (seenIds.has(moduleId)) {
      throw new Error(`Tenant module catalog contains duplicate module id "${moduleId}".`);
    }
    seenIds.add(moduleId);
  });

  const visit = (moduleId, trail = []) => {
    if (visited.has(moduleId)) return;
    if (stack.has(moduleId)) {
      throw new Error(`Tenant module catalog dependency cycle detected: ${[...trail, moduleId].join(' -> ')}.`);
    }

    const metadata = modulesById[moduleId];
    if (!metadata) {
      throw new Error(`Tenant module catalog references unknown module "${moduleId}".`);
    }

    stack.add(moduleId);
    (metadata.dependencies || []).forEach((dependencyId) => {
      if (!modulesById[dependencyId]) {
        throw new Error(`Tenant module "${moduleId}" depends on unknown module "${dependencyId}".`);
      }
      visit(dependencyId, [...trail, moduleId]);
    });
    stack.delete(moduleId);
    visited.add(moduleId);
  };

  catalog.forEach((module) => visit(module.id));
  return catalog;
}

export const MODULE_CATALOG = validateModuleCatalog(MODULE_CATALOG_JSON);
export const MODULES_BY_ID = buildModulesById(MODULE_CATALOG);

export function getModuleCatalog() {
  return MODULE_CATALOG;
}

export function getModuleMetadata(moduleId) {
  return MODULES_BY_ID[moduleId] || null;
}

export function getDefaultEnabledModules() {
  return MODULE_CATALOG
    .filter((module) => module.defaultEnabled && module.availability === MODULE_AVAILABILITY.ENABLED)
    .map((module) => module.id);
}

function addModuleWithDependencies(moduleId, seen, normalized, trail = []) {
  const metadata = getModuleMetadata(moduleId);
  if (!metadata) return;
  if (trail.includes(moduleId)) {
    throw new Error(`Tenant module dependency cycle detected: ${[...trail, moduleId].join(' -> ')}.`);
  }
  (metadata.dependencies || []).forEach((dependencyId) => {
    addModuleWithDependencies(dependencyId, seen, normalized, [...trail, moduleId]);
  });
  if (!seen.has(moduleId)) {
    seen.add(moduleId);
    normalized.push(moduleId);
  }
}

export function normalizeEnabledModules(value, { fallbackToDefaults = true } = {}) {
  const input = Array.isArray(value) ? value : (fallbackToDefaults ? getDefaultEnabledModules() : []);
  const normalized = [];
  const seen = new Set();

  input.forEach((candidate) => {
    const moduleId = String(candidate || '').trim();
    if (!moduleId || !getModuleMetadata(moduleId)) {
      return;
    }
    addModuleWithDependencies(moduleId, seen, normalized);
  });

  MODULE_CATALOG.forEach((module) => {
    if (module.locked) {
      addModuleWithDependencies(module.id, seen, normalized);
    }
  });

  return normalized;
}

export function buildEnabledModuleSet(value, options = {}) {
  return new Set(normalizeEnabledModules(value, options));
}

export function isModuleEnabled(value, moduleId) {
  if (!moduleId) return true;
  return buildEnabledModuleSet(value).has(moduleId);
}

export function getTransitiveDependencies(moduleId, seen = new Set()) {
  const metadata = getModuleMetadata(moduleId);
  if (!metadata) return [];

  const dependencies = [];
  (metadata.dependencies || []).forEach((dependencyId) => {
    if (seen.has(dependencyId)) return;
    seen.add(dependencyId);
    dependencies.push(dependencyId, ...getTransitiveDependencies(dependencyId, seen));
  });
  return dependencies;
}

export function getDependentModules(moduleId) {
  return MODULE_CATALOG.filter((module) => getTransitiveDependencies(module.id).includes(moduleId));
}

export function getMissingDependencies(moduleId, enabledModules) {
  const metadata = getModuleMetadata(moduleId);
  if (!metadata) return [];
  const enabledSet = enabledModules instanceof Set ? enabledModules : buildEnabledModuleSet(enabledModules);
  return getTransitiveDependencies(moduleId).filter((dependencyId) => !enabledSet.has(dependencyId));
}

export function getBlockingDependents(moduleId, enabledModules) {
  const enabledSet = enabledModules instanceof Set ? enabledModules : buildEnabledModuleSet(enabledModules);
  return getDependentModules(moduleId).filter((module) => enabledSet.has(module.id));
}

export function enableModuleWithDependencies(enabledModules, moduleId) {
  const enabledSet = enabledModules instanceof Set ? new Set(enabledModules) : buildEnabledModuleSet(enabledModules);
  const normalized = Array.from(enabledSet);
  const seen = new Set(enabledSet);
  addModuleWithDependencies(moduleId, seen, normalized);
  MODULE_CATALOG.forEach((module) => {
    if (module.locked) {
      addModuleWithDependencies(module.id, seen, normalized);
    }
  });
  return new Set(normalized);
}

export function disableModuleIfAllowed(enabledModules, moduleId) {
  const enabledSet = enabledModules instanceof Set ? new Set(enabledModules) : buildEnabledModuleSet(enabledModules);
  const metadata = getModuleMetadata(moduleId);
  if (!metadata || metadata.locked) {
    return {
      changed: false,
      enabledSet,
      reason: metadata?.locked ? `${metadata.name} is always enabled.` : 'Unknown module.'
    };
  }
  const blockingDependents = getBlockingDependents(moduleId, enabledSet);
  if (blockingDependents.length) {
    return {
      changed: false,
      enabledSet,
      reason: `${metadata.name} is required by ${blockingDependents.map((module) => module.name).join(', ')}.`
    };
  }
  enabledSet.delete(moduleId);
  return {
    changed: true,
    enabledSet,
    reason: ''
  };
}

export function getModuleStatus(module, enabledSet) {
  if (module.availability === MODULE_AVAILABILITY.COMING_SOON) return MODULE_AVAILABILITY.COMING_SOON;
  if (module.availability === MODULE_AVAILABILITY.DEPRECATED) return MODULE_AVAILABILITY.DEPRECATED;
  return enabledSet.has(module.id) ? MODULE_AVAILABILITY.ENABLED : MODULE_AVAILABILITY.DISABLED;
}

export function buildResolvedModuleCatalog(enabledModules) {
  const enabledSet = enabledModules instanceof Set ? enabledModules : buildEnabledModuleSet(enabledModules);
  return MODULE_CATALOG.map((module) => {
    const missingDependencies = getMissingDependencies(module.id, enabledSet);
    const blockingDependents = getBlockingDependents(module.id, enabledSet);
    return {
      ...module,
      status: getModuleStatus(module, enabledSet),
      enabled: enabledSet.has(module.id),
      missingDependencies,
      blockingDependents
    };
  });
}

export function groupModulesByCategory(enabledModules) {
  return buildResolvedModuleCatalog(enabledModules).reduce((accumulator, module) => {
    if (!accumulator[module.category]) {
      accumulator[module.category] = [];
    }
    accumulator[module.category].push(module);
    return accumulator;
  }, {});
}

export function filterNavigationByEnabledModules(navGroups, enabledModules, getModuleIdForPath) {
  const enabledSet = enabledModules instanceof Set ? enabledModules : buildEnabledModuleSet(enabledModules);
  return (navGroups || [])
    .map((group) => ({
      ...group,
      items: (group.items || []).filter((item) => {
        const moduleId = typeof getModuleIdForPath === 'function' ? getModuleIdForPath(item.path) : null;
        if (!moduleId) return true;
        return enabledSet.has(moduleId);
      })
    }))
    .filter((group) => (group.items || []).length > 0);
}

export function resolveModuleIdForPath(path) {
  if (!path) return null;
  if (path === '/today') return 'tasks';
  if (path === '/executive' || path === '/my-dashboard' || path === '/station-dashboard' || path === '/station-summary' || path === '/director-dashboard') return 'dashboards';
  if (path === '/alerts' || path === '/notifications') return 'notifications';
  if (path === '/requests-calendar' || path === '/approvals' || path === '/policies') return 'requests';
  if (path === '/goals' || path === '/goals/plan' || path === '/allocations' || path === '/actuals') return 'goals';
  if (path === '/location-scorecard' || path === '/kpis' || path === '/admin-kpis') return 'scorecards';
  if (path === '/employees/documents' || path === '/employees/expiring') return 'documents';
  if (path === '/workforce' || path === '/employees' || path === '/employees/profiles' || path === '/managers' || path === '/ops-managers') return 'workforce';
  if (path === '/audit') return 'audits';
  if (path === '/admin-reporting') return 'reports';
  if (path === '/integrations') return 'integrations';
  if (path === '/users' || path === '/locations' || path === '/location-setup' || path === '/admin-workspace' || path === '/admin-users' || path === '/admin-locations' || path === '/admin-delegations' || path === '/admin-tenant-configuration') return 'administration';
  if (/^\/employees\/profile\/\d+$/.test(path) || /^\/employees\/profile\/by-profile\/\d+$/.test(path) || /^\/workforce\/employees\/\d+$/.test(path)) return 'workforce';
  return null;
}
